import Chip from '@/components/elements/Chip';
import Icon from '@/components/icons/Icon';
import { APP_ROUTES } from '@/constants/pages';
import { classnames } from '@/helpers';
import { locationSlug } from '@/helpers/urlTs';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
} from '@/hooks/search/useSearch.constants';
import { BaseSearchManagerOutput } from '@/hooks/search/useSearch.types';
import useMobileView from '@/hooks/useMobileView';
import { Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import Filter from '../../components/Filter/Filter';
import { getSelectedFilters } from '../../components/Filter/Filter.helpers';
import { FilterManagerOutput } from '../../components/Filter/Filter.types';
import Sorting from '../../components/Sorting/Sorting';
import { SortingManagerOutput } from '../../components/Sorting/Sorting.types';
import { SearchControlsManagerOutput } from './SearchControls.hooks';

export type SearchControlProps = SearchControlsManagerOutput & {
  showSortingToggle?: boolean;
  showFilterToggle?: boolean;
  showSistaminutenToggle?: boolean;
  searchManager: BaseSearchManagerOutput;
  filterManager: FilterManagerOutput;
  sortingManager: SortingManagerOutput;
};

const SearchControls = (props: SearchControlProps) => {
  const { showFilterToggle = true, showSortingToggle = true, showSistaminutenToggle = true, searchManager } = props;
  const { isMobileView } = useMobileView();
  const history = useHistory();
  const selectionOptions = getSelectedFilters(searchManager.prefs);

  const isSistaminutenSearch = searchManager.type === 'sistaminuten';
  const isNearMeSearch =
    searchManager.type === 'default' && locationSlug(searchManager.location || '') === DEFAULT_MY_LOCATION_KEYWORD;

  const sistaminutenToggle = (
    <button
      onClick={() => {
        const keyword = searchManager.q || DEFAULT_SEARCH_KEYWORD;
        const location = searchManager.location || DEFAULT_SEARCH_LOCATION;
        let to = `/${keyword}/${location}`;

        if (!isSistaminutenSearch) {
          to = APP_ROUTES.SISTA_MINUTEN_SERP + to;
        }

        history.push(to);
      }}
      className={classnames(
        'text-m border-border_regular px-xs relative flex h-[32px] items-center justify-center rounded-lg border outline-none',
        'space-x-xxs relative flex items-center rounded-full border',
        isSistaminutenSearch ? 'bg-primary border-border_selected' : 'bg-surface_l2 border-border_regular',
      )}>
      <Icon variant="smt-filled" color="bg_accent_5" size="xs" />
      <span className={classnames(isSistaminutenSearch ? 'text-fg_primary_inverse' : 'text-fg_primary')}>
        Sista Minuten
      </span>
      {isSistaminutenSearch && (
        <button>
          <Icon variant="close" color="fg_primary_inverse" />
        </button>
      )}
    </button>
  );

  return (
    <Fragment>
      <div className="gap-sm flex flex-col items-baseline">
        <ul className="space-x-sm !relative z-10 flex">
          {showSortingToggle && (
            <li className="shrink-0">
              <Sorting
                {...props.sortingManager}
                loading={props.isLoadingGeoLocation}
                searchType={searchManager.type}
                isNearMeSearch={isNearMeSearch}
              />
            </li>
          )}
          {showFilterToggle && (
            <li className="shrink-0">
              <Filter {...props.filterManager} searchType={searchManager.type} />
            </li>
          )}
          {!isMobileView && showSistaminutenToggle && <li className="shrink-0">{sistaminutenToggle}</li>}
          {!isMobileView && selectionOptions.length > 0 && (
            <li>
              <ul className="gap-sm no-scrollbar flex overflow-x-scroll rounded-sm">
                {selectionOptions.map((selectedOption) => (
                  <li key={selectedOption.id} className="flex-shrink-0">
                    <Chip label={selectedOption.label} onClose={props.onRemoveSingleFilter(selectedOption.id)} />
                  </li>
                ))}
              </ul>
            </li>
          )}
        </ul>
        {isMobileView && showSistaminutenToggle && sistaminutenToggle}
      </div>
    </Fragment>
  );
};

export default SearchControls;
